import { useEffect, useState, useCallback, useRef, forwardRef } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";

import { Button } from "@chakra-ui/button";
import { Img } from "@chakra-ui/image";
import { Box, HStack, Stack, VStack } from "@chakra-ui/layout";
import { Input, InputGroup, InputLeftElement, Spinner, useToast } from "@chakra-ui/react";
import { Field, useFormikContext } from "formik";
import { round, debounce } from "lodash";

import Card from "@/components/Common/Card";
import Typography from "@/components/Common/Typography";
import V2Service from "@/services/v2Service";
import { goToPreviousStep, setSendAmount, setReceivedAmount } from "@/store/slices/sendMoneySlices";
import { currencyFormatter } from "@/utils/formatter";
import { saveDataToSession } from "@/utils/session-storing-utils";

// CSS
import "../../sendmoney.css";

const SetAmountTab = (props) => {
  const { isSubmitting } = props;
  const { setFieldValue, setFieldError, setFieldTouched } = useFormikContext();

  const dispatch = useDispatch();
  const toast = useToast();

  const leftFieldRef = useRef(null);
  const rightFieldRef = useRef(null);

  const [quotesState, setQuotesState] = useState(true);
  const [apiLoading, setApiLoading] = useState(false);
  const [fieldChange, setFieldChange] = useState(false);
  const [fxRate, setFxRate] = useState(0);
  const [payoutFees, setPayoutFees] = useState(0);
  const [direction, setDirection] = useState("forward");

  const balanceDetails = useSelector((state) => state.sendMoney.values.walletName, shallowEqual) || {};
  const paymentAccountDetails = useSelector((state) => state.sendMoney.values.payoutCurrency, shallowEqual) || {};
  const paymentMethodDetails = useSelector((state) => state.sendMoney.values.paymentCode, shallowEqual) || {};
  const sendAmount = useSelector((state) => state.sendMoney.sendAmount);
  const receivedAmount = useSelector((state) => state.sendMoney.receivedAmount);

  function handleBack() {
    dispatch(goToPreviousStep());
    setFieldValue("enteredAmount", 0); // Resetting states on clicking back
    setFieldValue("receivedAmount", 0);
    dispatch(setSendAmount(0));
    dispatch(setReceivedAmount(0));
  }

  const handleToast = (title, type) => {
    toast({
      title,
      status: type,
      duration: 5000,
      isClosable: true,
      position: "top",
    });
  };

  const getQuotes = useCallback(
    debounce(async (direction = "forward") => {
      let errorMessageForField = "";
      if (Number(sendAmount) === 0 && Number(receivedAmount) === 0) {
        return;
      }

      try {
        setApiLoading(true);
        if (sendAmount && Number(sendAmount) !== 0 && direction === "reverse") {
          const filters = {
            direction: "reverse",
            amount: sendAmount,
            currency: paymentAccountDetails.value,
            balanceCurrency: balanceDetails.value,
            paymentCode: paymentMethodDetails?.value,
          };
          const {
            data: { data: response },
          } = (await V2Service.getExchangeRate(filters)) || {};
          const parsedResponse = JSON.parse(response);
          const {
            fiatAmount,
            fees: { totalFee = 0 },
            exchangeRate = 0,
            quoteId = "",
          } = parsedResponse;

          setFxRate(round(exchangeRate, 6));
          setPayoutFees(totalFee);
          saveDataToSession("payout-quotes-data", "fxRate", round(exchangeRate, 6));
          saveDataToSession("payout-quotes-data", "processingFee", Number(totalFee));
          setFieldValue("quoteId", quoteId);

          dispatch(
            setReceivedAmount(paymentAccountDetails.value === "BTC" ? currencyFormatter(fiatAmount, 5, false) : currencyFormatter(fiatAmount, 2, false)),
          );
          setFieldValue("receivedAmount", Number(fiatAmount) || 0);

          setApiLoading(false);
        } else if (receivedAmount && Number(receivedAmount) !== 0 && direction === "forward") {
          const filters = {
            direction: "forward",
            amount: receivedAmount,
            currency: paymentAccountDetails.value,
            balanceCurrency: balanceDetails.value,
            paymentCode: paymentMethodDetails?.value,
          };
          const {
            data: { data: response },
          } = (await V2Service.getExchangeRate(filters)) || {};
          const parsedResponse = JSON.parse(response);

          const {
            depositAmount,
            fees: { totalFee = 0 },
            exchangeRate = 0,
            quoteId = "",
          } = parsedResponse;

          setFieldValue("quoteId", quoteId);
          setFxRate(exchangeRate, 6);
          setPayoutFees(totalFee);
          saveDataToSession("payout-quotes-data", "fxRate", round(exchangeRate, 6));
          saveDataToSession("payout-quotes-data", "processingFee", Number(totalFee));

          const valueToBeSet = balanceDetails?.value === "BTC" ? currencyFormatter(depositAmount, 5, false) : currencyFormatter(depositAmount, 2, false);
          dispatch(setSendAmount(valueToBeSet));
          setFieldValue("enteredAmount", Number(valueToBeSet) || 0);

          setApiLoading(false);
        }
      } catch (error) {
        const errorObject = error?.response?.data?.error?.message || {};
        const errorMessage = errorObject?.message || "Something went wrong";
        handleToast(errorMessage, "error");

        dispatch(setReceivedAmount(0));
        dispatch(setSendAmount(0));
      } finally {
        setApiLoading(false);
        setFieldChange(false);

        await setFieldTouched("enteredAmount", true, true);
        setFieldError("enteredAmount", errorMessageForField);
      }
    }, 500),
    [sendAmount, receivedAmount, paymentAccountDetails, balanceDetails, paymentMethodDetails],
  );

  useEffect(() => {
    let timeoutId;

    if (direction === "reverse") {
      timeoutId = setTimeout(() => {
        getQuotes(direction);
      }, 1000);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [sendAmount]);

  useEffect(() => {
    let timeoutId;

    if (direction === "forward") {
      timeoutId = setTimeout(() => {
        getQuotes(direction);
      }, 1000);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [receivedAmount]);

  useEffect(() => {
    //whenever any field gets changed, they gets disabled until API response
    //this code ensure that previous field gets refocused after that
    if (!fieldChange) {
      if (direction === "reverse") {
        leftFieldRef.current.focus();
      } else if (direction === "forward") {
        rightFieldRef.current.focus();
      }
    }
  }, [fieldChange]);

  return (
    <Card>
      <HStack w={"100%"} display={"flex"} justifyContent={"space-between"}>
        <Typography type="body" weight="medium" color="primary">
          {"Set Amount"}
        </Typography>
        <Box display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"center"} gap={"10px"}>
          <Typography type="caption" weight="medium" color="headertext">
            {"Balance"}:
          </Typography>
          <Typography type="body" weight="medium" color="headertext">
            {`${balanceDetails.label} ${balanceDetails.label === "BTC" ? currencyFormatter(balanceDetails.balance, 5) : currencyFormatter(balanceDetails.balance)}`}
          </Typography>
        </Box>
      </HStack>

      <HStack w={"100%"} display={"flex"} justifyContent={"space-between"} alignItems={"flex-start"}>
        <VStack align={"flex-start"}>
          <Typography type="description" weight="regular" color="secondary" colorweight="800">
            {"You Send"}
          </Typography>
          <Box className="sa-body-box">
            <Box display={"flex"} justifyContent={"flex-start"} alignItems={"center"} gap={"16px"}>
              <Img src={balanceDetails?.logo} alt={"logo"} w={"24px"} h={"24px"} />
              <Typography type="body" weight="regular" color="secondary" colorweight="900">
                {`${balanceDetails.label}`}
              </Typography>
            </Box>

            <Box>
              <Field name="enteredAmount">
                {({ field, form }) => (
                  <CustomTextField
                    type="number"
                    name="enteredAmount"
                    placeholder={"Enter Amount"}
                    w={"151px"}
                    h={"40px"}
                    fontWeight={"400"}
                    fontSize={"14px"}
                    textAlign={"right"}
                    marginTop={"2px"}
                    marginBottom={"5px"}
                    value={sendAmount}
                    ref={leftFieldRef}
                    onChange={(event) => {
                      const { value } = event.target;
                      const regex = balanceDetails?.label === "BTC" ? /^\d+(\.\d{0,5})?$/ : /^\d+(\.\d{0,2})?$/;
                      if (!value || regex.test(value)) {
                        setFieldChange(true);
                        dispatch(setSendAmount(value));
                        setDirection((prev) => "reverse");
                        field.onChange(event);
                      }
                    }}
                    //direction makes sure that only opposite textfield is showing loader
                    loading={apiLoading && direction === "forward"}
                    isDisabled={
                      apiLoading || //disable field when api is loading
                      //this ensures that opposite field gets disabled when we start typing on field
                      (fieldChange && direction === "forward")
                    }
                  />
                )}
              </Field>
            </Box>
          </Box>
        </VStack>

        <VStack align={"flex-start"}>
          <Typography type="description" weight="regular" color="secondary" colorweight="800">
            {"They Receive"}
          </Typography>

          <Box className="sa-body-box">
            <Box display={"flex"} justifyContent={"flex-start"} alignItems={"center"} gap={"16px"}>
              <Img src={paymentAccountDetails?.logo} alt={"logo"} w={"24px"} h={"24px"} />
              <Typography type="body" weight="regular" color="secondary" colorweight="900">
                {`${paymentAccountDetails?.value}`}
              </Typography>
            </Box>

            <Box>
              <Field name="receivedAmount">
                {({ field, form }) => (
                  <CustomTextField
                    type="number"
                    name="receivedAmount"
                    placeholder={"Enter Amount"}
                    w={"151px"}
                    h={"40px"}
                    fontWeight={"400"}
                    fontSize={"14px"}
                    textAlign={"right"}
                    marginBottom={"6px"}
                    value={receivedAmount}
                    ref={rightFieldRef}
                    onChange={(event) => {
                      const { value } = event.target;
                      const regex = paymentAccountDetails?.value === "BTC" ? /^\d+(\.\d{0,5})?$/ : /^\d+(\.\d{0,2})?$/;
                      if (!value || regex.test(value)) {
                        setFieldChange(true);
                        dispatch(setReceivedAmount(value));
                        setDirection((prev) => "forward");
                        field.onChange(event);
                      }
                    }}
                    //direction makes sure that only opposite textfield is showing loader
                    loading={apiLoading && direction === "reverse"}
                    isDisabled={
                      apiLoading || //disable field when api is loading
                      //this ensures that opposite field gets disabled when we start typing on field
                      (fieldChange && direction === "reverse")
                    }
                  />
                )}
              </Field>
            </Box>
          </Box>

          <Box w={"100%"} display={"flex"} textAlign={"right"} flexDirection={"column"} justifyContent={"flex-end"} gap={"8px"}>
            <Typography
              type="caption"
              weight="regular"
              color="primary"
              textDecoration={"underline"}
              cursor={"pointer"}
              onClick={() => {
                setQuotesState((prev) => !prev);
              }}
            >
              {quotesState ? "Hide Fee" : "Show Fee"}
            </Typography>
            {quotesState && (
              <>
                <Typography type="caption" weight="regular">
                  {"FX Rate"} {balanceDetails.label} = {paymentAccountDetails?.value === "BTC" ? currencyFormatter(fxRate, 5) : currencyFormatter(fxRate, 3)}{" "}
                  {paymentAccountDetails?.value}
                </Typography>
                <Typography type="caption" weight="regular">
                  {"Payout Fee"}: {balanceDetails.label === "BTC" ? currencyFormatter(payoutFees, 5) : currencyFormatter(payoutFees)}{" "}
                  {paymentAccountDetails.value}
                </Typography>
              </>
            )}
          </Box>
        </VStack>
      </HStack>

      <Stack direction="row" gap={"20px"} justifyContent={"end"} w="100%" marginTop={0}>
        <Button variant={"secondary"} size={"sm"} type="button" p={"9px 16px 9px 16px"} onClick={handleBack}>
          {"Back"}
        </Button>
        <Button
          variant={"primary"}
          type="submit"
          size={"sm"}
          p={"9px 16px 9px 16px"}
          isDisabled={fieldChange || (Number(sendAmount) || 0) === 0 || (Number(receivedAmount) || 0) === 0}
          isLoading={apiLoading || isSubmitting}
        >
          {"Next"}
        </Button>
      </Stack>
    </Card>
  );
};

const CustomTextField = forwardRef(({ loading, ...rest }, ref) => {
  return (
    <>
      <InputGroup>
        {loading && (
          <InputLeftElement>
            <Spinner size={"sm"} />
          </InputLeftElement>
        )}
        <Input ref={ref} {...rest}></Input>
      </InputGroup>
    </>
  );
});

export default SetAmountTab;
